"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FontFamilyToggle = exports.FontFamily = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const ui_common_components_1 = require("@desygner/ui-common-components");
const ui_common_translation_1 = require("@desygner/ui-common-translation");
const store_1 = require("../../store");
const FontDropdown_1 = require("./FontDropdown");
const FontFamily_style_1 = require("./FontFamily.style");
const FontFamily_utils_1 = require("./FontFamily.utils");
const LanguageDropdown_1 = require("./LanguageDropdown");
function FontFamily({ onUpdate, fonts, loadFontFace, localeSubsets, }) {
    const { t } = (0, ui_common_translation_1.useTranslation)();
    const rootRef = (0, react_1.useRef)(null);
    const { recentFonts, selectedFont } = (0, store_1.useMenu)();
    const menuDispatch = (0, store_1.useMenuDispatch)();
    const theme = (0, ui_common_components_1.useTheme)();
    const [currentLanguage, setCurrentLanguage] = (0, react_1.useState)('default');
    const useableFonts = (0, react_1.useMemo)(() => {
        const filteredFonts = fonts.filter((fontData) => !fontData.isSystem || !fontData.isEmbedded);
        return filteredFonts.reduce((uniqueFonts, currentFont) => {
            const isDuplicate = uniqueFonts.findIndex((font) => font.family === currentFont.family) !== -1;
            if (!isDuplicate) {
                uniqueFonts.push(currentFont);
            }
            return uniqueFonts;
        }, []);
    }, [fonts]);
    const [currentUsableFonts, setCurrentUsableFonts] = (0, react_1.useState)(useableFonts);
    const regularFonts = (0, react_1.useMemo)(() => {
        const isRegularFont = function (font) {
            return !font.isSystem && !(font.customId || font.templateFonts);
        };
        return currentUsableFonts
            .filter(isRegularFont)
            .filter((font) => (0, FontFamily_utils_1.filterFontsWithLanguage)(font, currentLanguage))
            .map(FontFamily_utils_1.mapFonts)
            .sort(FontFamily_utils_1.orderFamily);
    }, [currentUsableFonts, currentLanguage]);
    const libraryFonts = (0, react_1.useMemo)(() => {
        const isLibraryFont = function (font) {
            return !font.isSystem && font.customId;
        };
        return currentUsableFonts
            .filter(isLibraryFont)
            .filter((font) => (0, FontFamily_utils_1.filterFontsWithLanguage)(font, currentLanguage))
            .map(FontFamily_utils_1.mapFonts)
            .sort(FontFamily_utils_1.orderFamily);
    }, [currentUsableFonts, currentLanguage]);
    const currentRecentFonts = (0, react_1.useMemo)(() => {
        return currentUsableFonts
            .filter((font) => recentFonts.includes(font.family))
            .filter((font) => (0, FontFamily_utils_1.filterFontsWithLanguage)(font, currentLanguage))
            .map(FontFamily_utils_1.mapFonts)
            .sort(FontFamily_utils_1.orderFamily);
    }, [currentUsableFonts, recentFonts, currentLanguage]);
    const [listSize, setListSize] = (0, react_1.useState)(0);
    const currentRegularFonts = (0, react_1.useMemo)(() => regularFonts.slice(0, listSize), [regularFonts, listSize]);
    const getFontsOnCanvas = (0, react_1.useCallback)(() => {
        const svgContent = document.querySelector('#svgcontent');
        if (!svgContent)
            return [];
        let fontsPresent = Array.from(svgContent.querySelectorAll('[font-family]')).map((e) => { var _a; return ((_a = e.getAttribute('font-family')) === null || _a === void 0 ? void 0 : _a.replace(/^'|'$/g, '')) || ''; });
        fontsPresent = [...new Set(fontsPresent)];
        return fontsPresent;
    }, []);
    (0, react_1.useEffect)(function setRecentFonts() {
        menuDispatch({
            type: 'SET_RECENT_FONTS',
            payload: {
                fonts: getFontsOnCanvas(),
            },
        });
    }, []);
    const updateRegularFontList = (0, react_1.useCallback)(() => {
        if (!rootRef.current)
            return;
        const { scrollTop, scrollHeight, clientHeight } = rootRef.current;
        if (scrollTop + clientHeight >= scrollHeight &&
            listSize < regularFonts.length) {
            setListSize((prevSize) => prevSize + 20);
        }
    }, [regularFonts.length, listSize]);
    (0, react_1.useEffect)(function addScrollListener() {
        const currentRef = rootRef.current;
        if (!currentRef)
            return;
        currentRef.addEventListener('scroll', updateRegularFontList);
        return () => {
            if (!currentRef)
                return;
            currentRef.removeEventListener('scroll', updateRegularFontList);
        };
    }, [updateRegularFontList]);
    (0, react_1.useEffect)(updateRegularFontList, [rootRef.current, listSize]);
    const handleUpdate = (0, react_1.useCallback)((data) => {
        onUpdate(data);
        const fontData = data;
        menuDispatch({
            type: 'SET_RECENT_FONTS',
            payload: {
                fonts: [fontData.fontFamily],
            },
        });
    }, [menuDispatch, onUpdate]);
    const onSearch = (0, react_1.useCallback)((value) => {
        setCurrentUsableFonts(useableFonts.filter((font) => font.family.toLowerCase().includes(value.toLowerCase())));
    }, [useableFonts]);
    const onLanguageChange = (0, react_1.useCallback)((value) => {
        setCurrentLanguage(value);
    }, []);
    return ((0, jsx_runtime_1.jsxs)(FontFamily_style_1.StyledContainer, { children: [(0, jsx_runtime_1.jsxs)(FontFamily_style_1.StyledSearchContainer, { children: [(0, jsx_runtime_1.jsx)(ui_common_components_1.SearchBox, { id: 'font-filter', placeholder: t('font.panel.search_placeholder'), onChange: onSearch }), (0, jsx_runtime_1.jsx)(LanguageDropdown_1.LanguageDropdown, { onSelect: onLanguageChange, localeSubsets: localeSubsets })] }), (0, jsx_runtime_1.jsxs)(FontFamily_style_1.StyledFontsContainer, { ref: rootRef, children: [(0, jsx_runtime_1.jsxs)(FontFamily_style_1.StyledTitleContainer, { children: [(0, jsx_runtime_1.jsx)(ui_common_components_1.Icon, { name: 'time', color: theme.Color.Neutrals.Dark }), (0, jsx_runtime_1.jsx)(ui_common_components_1.Text, { size: 'Body', color: theme.Color.Neutrals.Dark, children: t('font.panel.recent_used') })] }), currentRecentFonts.map((font, index) => ((0, jsx_runtime_1.jsx)(FontDropdown_1.FontDropdown, { rootRef: rootRef, family: font.family, selectedFamily: selectedFont, variants: font.variants, onUpdate: handleUpdate, loadFontFace: loadFontFace }, index))), (0, jsx_runtime_1.jsx)(ui_common_components_1.Divider, {}), (0, jsx_runtime_1.jsxs)(FontFamily_style_1.StyledTitleContainer, { children: [(0, jsx_runtime_1.jsx)(ui_common_components_1.Icon, { name: 'text-indicator', color: theme.Color.Neutrals.Dark }), (0, jsx_runtime_1.jsx)(ui_common_components_1.Text, { size: 'Body', color: theme.Color.Neutrals.Dark, children: t('font.panel.library_fonts') })] }), libraryFonts.map((font, index) => ((0, jsx_runtime_1.jsx)(FontDropdown_1.FontDropdown, { rootRef: rootRef, family: font.family, selectedFamily: selectedFont, variants: font.variants, onUpdate: handleUpdate, loadFontFace: loadFontFace }, index))), (0, jsx_runtime_1.jsx)(ui_common_components_1.Divider, {}), (0, jsx_runtime_1.jsxs)(FontFamily_style_1.StyledTitleContainer, { children: [(0, jsx_runtime_1.jsx)(ui_common_components_1.Icon, { name: 'text-indicator', color: theme.Color.Neutrals.Dark }), (0, jsx_runtime_1.jsx)(ui_common_components_1.Text, { size: 'Body', color: theme.Color.Neutrals.Dark, children: t('font.panel.all_fonts') })] }), currentRegularFonts.map((font, index) => ((0, jsx_runtime_1.jsx)(FontDropdown_1.FontDropdown, { rootRef: rootRef, family: font.family, selectedFamily: selectedFont, variants: font.variants, onUpdate: handleUpdate, loadFontFace: loadFontFace }, index)))] })] }));
}
exports.FontFamily = FontFamily;
function FontFamilyToggle({ selectedFont }) {
    const { SECTION } = (0, store_1.useMenu)();
    const theme = (0, ui_common_components_1.useTheme)();
    const isSectionVisible = (0, react_1.useMemo)(() => SECTION.isVisible && SECTION.name === 'text_fontfamily', [SECTION]);
    return ((0, jsx_runtime_1.jsxs)(FontFamily_style_1.StyledFontFamilyToggleContainer, { isSectionVisible: isSectionVisible, children: [(0, jsx_runtime_1.jsx)(FontFamily_style_1.StyledToggleTextWrapper, { children: (0, jsx_runtime_1.jsx)(ui_common_components_1.Text, { size: 'Body', color: isSectionVisible
                        ? theme.Color.Brand.Primary
                        : theme.Color.Neutrals.Dark, children: selectedFont }) }), (0, jsx_runtime_1.jsx)(ui_common_components_1.Icon, { name: 'chevron---down', color: isSectionVisible
                    ? theme.Color.Brand.Primary
                    : theme.Color.Neutrals.Dark })] }));
}
exports.FontFamilyToggle = FontFamilyToggle;
//# sourceMappingURL=FontFamily.js.map